import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Database, GitBranch, Radio, Telescope } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/components/providers/auth-provider';
import { useUiStore, type Lang } from '@/stores/ui-store';
import { formatNumber, t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * Stats section content for the AppShell right rail.
 *
 * Default body — graph corpus stats (entities / relationships / sources /
 * density / by-label / top-relationship breakdowns). Pages that want to
 * surface page-local live stats can pass `liveStats` (an array of small
 * tiles rendered under a "Live" subhead) — that's how the command center
 * shows watchlist + signals counts here.
 */

export interface LiveStatTile {
  /** Unique key for the tile (label is fine if unique on the page). */
  key: string;
  icon: ReactNode;
  label: string;
  value: number;
  /** Pulse the icon ring — use for "something just happened" tiles. */
  pulse?: boolean;
}

export interface StatsPaneSectionProps {
  /** Optional live-stat tiles surfaced under the corpus stats. */
  liveStats?: LiveStatTile[];
}

export function StatsPaneSection({ liveStats }: StatsPaneSectionProps) {
  const lang = useUiStore((s) => s.lang);
  const numerals = useUiStore((s) => s.numerals);
  const { user, activeOrg } = useAuth();

  // Stats are org-scoped on the server (X-Org-Id header). Skip the fetch
  // when the user doesn't have an active org yet so we don't hammer /api/stats
  // on the sign-in or onboarding paths.
  const enabled = Boolean(user && activeOrg);
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats', activeOrg?.id ?? null],
    queryFn: () => api.stats(),
    staleTime: 30_000,
    enabled,
  });

  if (!enabled) {
    return (
      <div className="p-5">
        <p className="text-xs text-[var(--color-ink-muted)]">
          {t('pane.stats.empty', lang)}
        </p>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="p-5">
        <p className="text-xs text-[var(--color-ink-muted)]">
          {t('common.loading', lang)}
        </p>
      </div>
    );
  }

  const fmt = (n: number) => formatNumber(n, lang, numerals);
  const density = stats.entities > 0 ? stats.relationships / stats.entities : 0;
  const densityFmt = density.toLocaleString(
    lang === 'fa' ? 'fa-IR' : 'en-US',
    { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  );

  return (
    <div className="space-y-5 p-5 text-sm">
      {/* Hero totals — three stat tiles that read "this is what the corpus looks
          like at a glance." Density gets its own caption underneath because
          the ratio is the value that says "the graph is well-connected" rather
          than the raw rel count. */}
      <div className="grid grid-cols-3 gap-2">
        <HeroStat
          icon={<Boxes className="size-3.5" />}
          label={t('command-center.stats.entities', lang)}
          value={fmt(stats.entities)}
          accent="var(--color-accent)"
        />
        <HeroStat
          icon={<GitBranch className="size-3.5" />}
          label={t('command-center.stats.relationships', lang)}
          value={fmt(stats.relationships)}
          accent="var(--color-graph-org)"
        />
        <HeroStat
          icon={<Database className="size-3.5" />}
          label={t('command-center.stats.sources', lang)}
          value={fmt(stats.documents)}
          accent="var(--color-graph-reg)"
        />
      </div>

      <div className="rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)]/60 px-3 py-2 text-xs text-[var(--color-ink-muted)]">
        <div className="flex items-baseline justify-between gap-3">
          <span>{t('command-center.stats.density', lang)}</span>
          <span className="font-mono text-sm tabular-nums text-[var(--color-ink)]">
            {densityFmt}
          </span>
        </div>
        <p className="mt-0.5 text-xxs">
          {t('command-center.stats.density.note', lang)}
        </p>
      </div>

      {stats.byLabel.length > 0 && (
        <StatsBreakdown
          title={t('command-center.stats.by-label', lang)}
          rows={stats.byLabel.map((b) => ({
            label: b.label ?? t('command-center.stats.unlabeled', lang),
            count: b.count,
            color: graphLabelColor(b.label),
          }))}
          total={stats.entities}
          fmt={fmt}
        />
      )}

      {stats.topRelationships.length > 0 && (
        <StatsBreakdown
          title={t('command-center.stats.top-relationships', lang)}
          rows={stats.topRelationships.map((r) => ({
            label: r.rel,
            count: r.count,
          }))}
          total={stats.relationships}
          fmt={fmt}
          mono
        />
      )}

      {liveStats && liveStats.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-1.5 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
            <Radio className="size-3" aria-hidden />
            {t('command-center.stats.live', lang)}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {liveStats.map((s) => (
              <LiveStat
                key={s.key}
                icon={s.icon}
                label={s.label}
                value={fmt(s.value)}
                pulse={s.pulse}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Convenience helper — common live tiles for the command center. */
export function watchlistAndSignalsLiveStats(
  watchlistCount: number,
  signalsCount: number,
  lang: Lang,
): LiveStatTile[] {
  return [
    {
      key: 'watchlist',
      icon: <Telescope className="size-3.5" />,
      label: t('command-center.stats.watchlist', lang),
      value: watchlistCount,
    },
    {
      key: 'signals',
      icon: <Radio className="size-3.5" />,
      label: t('command-center.stats.signals', lang),
      value: signalsCount,
      pulse: signalsCount > 0,
    },
  ];
}

/** Map a Neo4j label to the design-system graph palette. */
function graphLabelColor(label: string | null): string {
  switch (label) {
    case 'Person':
      return 'var(--color-graph-person)';
    case 'Organization':
      return 'var(--color-graph-org)';
    case 'Government':
      return 'var(--color-graph-gov)';
    case 'Regulatory':
      return 'var(--color-graph-reg)';
    default:
      return 'var(--color-accent)';
  }
}

function HeroStat({
  icon,
  label,
  value,
  accent,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <div
      className="rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)] p-3"
      style={{ borderTopColor: accent, borderTopWidth: 2 }}
    >
      <div className="flex items-center gap-1.5 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
        <span className="text-[var(--color-ink-muted)]" style={{ color: accent }}>
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 font-serif text-2xl tabular-nums text-[var(--color-ink)]">
        {value}
      </div>
    </div>
  );
}

function LiveStat({
  icon,
  label,
  value,
  pulse,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  pulse?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-sm border border-[var(--color-border)] bg-[var(--color-elevated)] px-3 py-2">
      <span className="relative inline-flex size-7 items-center justify-center rounded-full bg-[var(--color-accent-softer)] text-[var(--color-accent)]">
        {icon}
        {pulse && (
          <span
            aria-hidden
            className="absolute inset-0 animate-ping rounded-full bg-[var(--color-accent)]/40"
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-base tabular-nums text-[var(--color-ink)]">
          {value}
        </div>
        <div className="truncate text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
          {label}
        </div>
      </div>
    </div>
  );
}

function StatsBreakdown({
  title,
  rows,
  total,
  fmt,
  mono,
}: {
  title: string;
  rows: Array<{ label: string; count: number; color?: string }>;
  total: number;
  fmt: (n: number) => string;
  mono?: boolean;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div>
      <h3 className="mb-2 text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
        {title}
      </h3>
      <div className="space-y-1.5">
        {rows.map((row) => {
          const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
          const color = row.color ?? 'var(--color-accent)';
          return (
            <div key={row.label} className="text-xs">
              <div className="mb-0.5 flex items-baseline justify-between gap-3">
                <span
                  className={cn(
                    'inline-flex min-w-0 items-center gap-1.5 truncate text-[var(--color-ink-secondary)]',
                    mono && 'font-mono',
                  )}
                  title={row.label}
                >
                  {row.color && (
                    <span
                      aria-hidden
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                  )}
                  <span className="truncate">{row.label}</span>
                </span>
                <span className="font-mono tabular-nums text-[var(--color-ink-muted)]">
                  {fmt(row.count)}{' '}
                  <span className="text-xxs opacity-60">({pct}%)</span>
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-[var(--color-border)]/40">
                <div
                  className="h-full"
                  style={{
                    width: `${(row.count / max) * 100}%`,
                    backgroundColor: `color-mix(in oklab, ${color} 75%, transparent)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

